/* eslint-disable no-unsafe-optional-chaining */
import { useMemo, useState } from 'react';
import { Card, CardBody, CardFooter } from '../../../../../components/card/card';
import { CardHeader } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import NoData from '@components/NoData/NoData';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { getPagedData, setFixedFilters, setPagination } from '@store/DynamicSlice/dynamicTableSlice';
import { DynamicPagination } from '@Helper/DynamicFilter/DynamicPagination';

interface Props {
  tableKey: string;
  data: any[];

  selectedOutMap: Record<string, any>;
  onToggleSelect: (item: any) => void;
  onSelectAllVisible: (visibleItems: any[]) => void;
  onDeselectAllVisible: (visibleItems: any[]) => void;
  onAddSelected: () => void;
}

const CARD_HEIGHT = 900;

const NULL_FIXED = {
  itemCode: null,
  name: null,
  categoryId: null,
  unitOfMeasure: null,
  brandId: null,
  barcode: null,
  itemType: null
};

const FILTER_OPTIONS = [
  { value: 'name', label: 'الاسم' },
  { value: 'itemCode', label: 'كود الصنف' },
  { value: 'barcode', label: 'الباركود' },
  { value: 'itemType', label: 'نوع الصنف' }
] as const;

type FilterKey = (typeof FILTER_OPTIONS)[number]['value'];

const getKey = (item: any) => String(item?.id ?? '');

const ItemsNotInInventory = ({
  tableKey,
  data,
  selectedOutMap,
  onToggleSelect,
  onSelectAllVisible,
  onDeselectAllVisible,
  onAddSelected
}: Props) => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();

  const table = useAppSelector(state => state.dynamicTable[tableKey]);
  const loading = table?.loading ?? false;
  const error = table?.error ?? null;
  const pageSize = table?.pageSize ?? 20;

  const [filterKey, setFilterKey] = useState<FilterKey>('name');
  const [filterText, setFilterText] = useState('');

  const currentData = data ?? [];
  const selectedCount = useMemo(() => Object.keys(selectedOutMap ?? {}).length, [selectedOutMap]);

  const isAllVisibleSelected = useMemo(() => {
    if (!currentData.length) return false;
    return currentData.every(it => !!selectedOutMap[getKey(it)]);
  }, [currentData, selectedOutMap]);

  const applySearch = () => {
    const text = filterText.trim();

    dispatch(
      setFixedFilters({
        tableKey,
        fixedFilters: {
          ...NULL_FIXED,
          [filterKey]: text ? text : null
        }
      })
    );

    dispatch(setPagination({ tableKey, pageIndex: 0, pageSize }));
    dispatch(getPagedData({ tableKey }));
  };

  const clearSearch = () => {
    setFilterText('');
    setFilterKey('name');

    dispatch(setFixedFilters({ tableKey, fixedFilters: { ...NULL_FIXED } }));
    dispatch(setPagination({ tableKey, pageIndex: 0, pageSize }));
    dispatch(getPagedData({ tableKey }));
  };

  return (
    <div className='col-lg-6 mb-1 d-flex' style={{ height: CARD_HEIGHT }}>
      <Card classNeme='w-100 d-flex flex-column' style={{ height: '100%' }}>
        <CardHeader className='fw-bold small bg-success bg-opacity-25'>
          <h6 className='m-1'>الأصنـاف الغير معيّنه في المخزن</h6>
        </CardHeader>

        <CardBody className='d-flex flex-column flex-grow-1' style={{ minHeight: 0 }}>
          {/* بحث ثابت */}
          <div className='d-flex gap-2 mb-3'>
            <select
              className='form-select'
              style={{ maxWidth: 180 }}
              value={filterKey}
              onChange={e => setFilterKey(e.target.value as FilterKey)}
            >
              {FILTER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <input
              className='form-control'
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              placeholder='اكتب قيمة الفلتر...'
              onKeyDown={e => {
                if (e.key === 'Enter') applySearch();
              }}
            />

            <button className='btn btn-theme' type='button' onClick={applySearch} disabled={loading}>
              <i className='bi bi-search me-1'></i> بحث
            </button>

            <button className='btn btn-outline-secondary' type='button' onClick={clearSearch} disabled={loading}>
              مسح
            </button>
          </div>

          {error ? <div className='alert alert-danger py-1 small mb-2'>{String(error)}</div> : null}

          {loading ? (
            <div className='flex-grow-1 d-flex justify-content-center align-items-center' style={{ minHeight: 0 }}>
              <div className='spinner-border spinner-border-sm text-theme me-2'></div>
              <span className='small text-muted'>جاري تحميل الأصناف...</span>
            </div>
          ) : (
            <>
              <div className='flex-grow-1' style={{ minHeight: 0 }}>
                {currentData.length ? (
                  <div className='table-responsive h-100' style={{ overflowY: 'auto' }}>
                    <table className='table table-hover mb-0'>
                      <tbody>
                        {currentData.map((item, idx) => {
                          const key = getKey(item);
                          const selected = !!selectedOutMap[key];

                          return (
                            <tr
                              key={key || idx}
                              style={{ cursor: 'pointer' }}
                              onClick={() => onToggleSelect(item)}
                              className={selected ? 'table-active' : ''}
                            >
                              <td className='align-middle'>
                                <div className='d-flex justify-content-between'>
                                  <div>
                                    <span className='fw-bold'>{i18n.language === 'en' ? item?.name : item?.name2}</span>
                                    {item?.itemCode ? <div className='small text-muted'>{item.itemCode}</div> : null}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className='h-100 d-flex align-items-center justify-content-center'>
                    <NoData />
                  </div>
                )}
              </div>

              <div className='mt-2'>
                <DynamicPagination tableKey={tableKey} maxVisiblePages={1} />
              </div>
            </>
          )}
        </CardBody>

        <CardFooter className='d-flex justify-content-between align-items-center'>
          <button className='btn btn-theme' onClick={onAddSelected} disabled={selectedCount === 0}>
            <i className='bi bi-arrow-left-circle me-2'></i>
            {t('Assign')} ({selectedCount})
          </button>

          <button
            onClick={() => (isAllVisibleSelected ? onDeselectAllVisible(currentData) : onSelectAllVisible(currentData))}
            className='btn btn-outline-theme'
            disabled={!currentData.length || loading}
          >
            {isAllVisibleSelected ? t('UnselectAll') : t('SelectAll')}
          </button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ItemsNotInInventory;
